import ImageBox from "@/components/image-box"
import { authStore } from "@/routes/login/auth-store"
import { langStore } from "@/stores/lang-store"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { loginBundle } from "./login-const"

// ! 초기 로딩 캐시 작업
const preloadedLogo = new Image()
preloadedLogo.src = "/images/toicos-logo-b.png"

const LoginPage = () => {
  const navigate = useNavigate()

  const { lang } = langStore()
  const [form, setForm] = useState({ user_id: "", password: "" })

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const success = await authStore.getState().apiLogin({ ...form })

      if (success) {
        const { sido_key, scope_type, scope_code, role } = authStore.getState()
        // 관리자
        if (role === "ADMIN") {
          navigate("/select", { replace: true })
          return
        }

        // 그룹 사용자
        if (scope_type === "group") {
          navigate("/select/apt", { replace: true })
          return
        }

        // 개별 아파트 사용자
        if (scope_type === "only") {
          const sidoLowerCase = sido_key.toLowerCase()

          navigate(`/apt/${sidoLowerCase}/${scope_code}/overview`, { replace: true })
          return
        }

        // 소속 정보가 없는 일반 사용자
        toast.warning("접근 가능한 소속 정보가 없습니다.")
      }
    } catch (err) {
      toast.warning(err?.response?.data?.detail ?? loginBundle.message[lang])
    }
  }

  return (
    <main className="min-h-screen w-full bg-linear-to-br from-slate-50 via-blue-50/30 to-slate-100 flex flex-col relative font-sans text-slate-900 overflow-hidden">
      {/* 화면 블러 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-cyan-300/5 rounded-full blur-2xl"></div>
      </div>

      {/* 중앙 정렬 컨테이너 */}
      <section className="flex-1 flex flex-col items-center justify-center p-4 relative z-10">
        {/* 로그인 폼 카드 */}
        <form
          className="
            w-full max-w-125
            bg-white/90 backdrop-blur-xl
            rounded-xl 
            border border-white/60
            shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)]
            py-8 px-4
            flex flex-col items-center
            ring-1 ring-slate-900/5
          "
          onSubmit={handleSubmit}
        >
          {/* 로고 영역 */}
          <div className="mb-10 md:mb-12">
            <ImageBox className="w-44 md:w-52 drop-shadow-md" src={"/images/toicos-logo-b.png"} />
          </div>

          {/* 인풋 영역 */}
          <div className="w-full flex flex-col gap-3 mb-8">
            {loginBundle.forms.map((item, idx) => {
              const { name, type, placeholder } = item
              return (
                <div key={name} className="relative group">
                  <input
                    name={name}
                    type={type}
                    placeholder={placeholder[lang]}
                    autoFocus={idx === 0}
                    onChange={handleChange}
                    value={form[name]}
                    className="
                      w-full p-3
                      bg-slate-50/80 backdrop-blur-sm
                      border border-slate-200/80
                      rounded-lg
                      text-base text-slate-800 placeholder:text-slate-400
                      outline-none transition-all duration-300
                      focus:bg-white focus:border-toicos-navy/60 focus:ring-4 focus:ring-blue-500/10
                      focus:shadow-lg focus:shadow-blue-500/5
                      hover:border-slate-300 hover:bg-white/60
                      font-medium
                    "
                  />
                </div>
              )
            })}
          </div>

          {/* 로그인 버튼 */}
          <button
            type="submit"
            className="
              w-full py-2 
              bg-linear-to-r from-toicos-navy to-blue-700
              text-white text-lg font-bold rounded-lg
              shadow-[0_8px_24px_-4px_rgba(30,58,138,0.3)]
              transform transition-all duration-300
              hover:shadow-[0_12px_32px_-4px_rgba(30,58,138,0.4)]
              hover:-translate-y-1
              hover:from-blue-700 hover:to-toicos-navy
              active:translate-y-0 active:shadow-[0_4px_16px_-2px_rgba(30,58,138,0.3)]
              relative overflow-hidden
              before:absolute before:inset-0 
              before:bg-linear-to-r before:from-transparent before:via-white/20 before:to-transparent
              before:-translate-x-full hover:before:translate-x-full
              before:transition-transform before:duration-700
            "
          >
            <span className="relative z-10">{loginBundle.login[lang]}</span>
          </button>
        </form>

        {/* 하단 카피라이트 */}
        <div className="mt-14 text-xs text-slate-500 font-medium tracking-wide">
          © 2026 ToICoS Co. All rights reserved.
        </div>
      </section>
    </main>
  )
}

export default LoginPage
